/**
 * Lichter als Billboard-Sprites. Aus Entfernung sieht man genau das: leuchtende Punkte,
 * keine beleuchteten Flächen. Der Schein entsteht aus zwei Sprites je Licht (Kern und Hof),
 * nicht aus einer Postprocessing-Kette, damit auch alte Geräte 60 fps halten.
 *
 * Die Sprites zeichnen ohne Tiefenprüfung (`depthTest: false`). Das ist Absicht:
 * Ueber die Sichtbarkeit entscheidet der vorgeschriebene Sektor, nicht unsere grobe
 * Rumpfgeometrie. Anlage I verlangt, dass jedes Licht in seinem ganzen Sektor frei
 * sichtbar ist; ein Licht, das am Modell hinter einem Aufbau verschwindet, wäre ein
 * Fehler des Modells, nicht der Regel. Die Prüfung in e2e/lights.spec.ts hält das fest.
 */
import { AdditiveBlending, CanvasTexture, Sprite, SpriteMaterial, Texture, Vector3 } from 'three'
import { apparentIntensity, nominalRangeNm, sectorVisibility } from '@/core/geometry'
import { alsZahl } from '@/core/lichtfarben'
import type { LengthClass, LightColour, LightSpec } from '@/core/types'
import type { VesselModel } from './hulls'

/**
 * Die Farben stehen in `src/core/lichtfarben.ts`, zusammen mit denen der Oberfläche.
 * Zwei Listen für dieselbe Sache sind auseinandergelaufen, und der Punkt in der Liste
 * zeigte ein anderes Weiß als das Licht im Bild.
 */
const COLOURS: Record<LightColour, number> = {
  white: alsZahl('white'),
  red: alsZahl('red'),
  green: alsZahl('green'),
  yellow: alsZahl('yellow'),
  blue: alsZahl('blue'),
}

/** Senkrechter Abstand zwischen zwei Lichtern an demselben Punkt (Anlage I). */
const TIER_SPACING_M = 2.5

function radialTexture(size: number, falloff: number): Texture {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('kein 2D-Kontext für die Lichttextur')
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
  for (let i = 0; i <= 10; i += 1) {
    const t = i / 10
    const a = Math.max(0, (1 - t) ** falloff)
    g.addColorStop(t, `rgba(255,255,255,${a.toFixed(4)})`)
  }
  ctx.fillStyle = g
  ctx.fillRect(0, 0, size, size)
  const tex = new CanvasTexture(canvas)
  tex.needsUpdate = true
  return tex
}

let coreTexture: Texture | undefined
let haloTexture: Texture | undefined

function textures(): { core: Texture; halo: Texture } {
  coreTexture ??= radialTexture(64, 2.2)
  haloTexture ??= radialTexture(128, 5)
  return { core: coreTexture, halo: haloTexture }
}

export interface RenderLight {
  spec: LightSpec
  colour: LightColour
  position: Vector3
  rangeNm: number
  core: Sprite
  halo: Sprite
  /** Zuletzt gerechnete Helligkeit, 0 bis 1. Grundlage der Prüfungen. */
  intensity: number
}

/**
 * Ankerpunkt plus Staffelung plus Seitenversatz der Seitenlichter.
 * Steuerbord liegt bei -X, siehe Kopf von hulls.ts.
 */
export function lightPosition(model: VesselModel, spec: LightSpec): Vector3 {
  const sides = model.sidelightAnchors
  if (sides && spec.type === 'sidelight-port') return sides.port.clone()
  if (sides && spec.type === 'sidelight-stbd') return sides.stbd.clone()
  const base = model.anchors[spec.mount].clone()
  if (spec.type === 'sidelight-port') base.x += model.sidelightOffsetM
  if (spec.type === 'sidelight-stbd') base.x -= model.sidelightOffsetM
  if (spec.tier) base.y += spec.tier * TIER_SPACING_M
  // Steuerbord liegt bei -X (siehe hulls.ts), deshalb das Minus.
  if (spec.versatzM) base.x -= spec.versatzM
  return base
}

export function createLights(
  model: VesselModel,
  specs: LightSpec[],
  lengthClass: LengthClass,
): RenderLight[] {
  const { core, halo } = textures()
  return specs.map((spec) => {
    const colour = COLOURS[spec.colour]
    const position = lightPosition(model, spec)
    const coreSprite = new Sprite(
      new SpriteMaterial({
        map: core,
        color: colour,
        blending: AdditiveBlending,
        depthWrite: false,
        sizeAttenuation: false,
        transparent: true,
        fog: false,
        depthTest: false,
      }),
    )
    const haloSprite = new Sprite(
      new SpriteMaterial({
        map: halo,
        color: colour,
        blending: AdditiveBlending,
        depthWrite: false,
        sizeAttenuation: false,
        transparent: true,
        fog: false,
        depthTest: false,
      }),
    )
    coreSprite.position.copy(position)
    haloSprite.position.copy(position)
    coreSprite.renderOrder = 10
    haloSprite.renderOrder = 9
    model.group.add(haloSprite)
    model.group.add(coreSprite)
    return {
      spec,
      colour: spec.colour,
      position,
      rangeNm: nominalRangeNm(spec.type, lengthClass),
      core: coreSprite,
      halo: haloSprite,
      intensity: 0,
    }
  })
}

export interface LightFrame {
  /** Peilung des Betrachters vom Fahrzeug aus, 0 = recht voraus. */
  bearingDeg: number
  distanceNm: number
  timeSec: number
  /** Am Tag brennen die Lichter zwar, sind aber kaum zu sehen. */
  dayFactor: number
}

/** Blinkverhalten am Fahrzeug: Funkellicht 120/min, gelbes Funkellicht 50 bis 70/min. */
function rhythmFactor(spec: LightSpec, timeSec: number): number {
  switch (spec.rhythm) {
    case 'flashing':
      return timeSec % 0.5 < 0.25 ? 1 : 0
    case 'special-flashing':
      return timeSec % 1 < 0.45 ? 1 : 0
    default:
      return 1
  }
}

const CORE_SCALE = 0.02
const HALO_SCALE = 0.075
const NM_IN_METERS = 1852

/**
 * Aus großer Entfernung ist ein Licht ein Glanzpunkt fester Größe, aus der Nähe
 * sieht man die Laterne selbst. Ohne diese Dämpfung verschmelzen zwei Lichter, die
 * 2,5 m auseinander hängen, im Nahbereich zu einem Fleck, und genau ihre Reihenfolge
 * ist der Inhalt (grün über weiß, rot-weiß-rot).
 */
function nearFactor(distanceNm: number): number {
  const meters = distanceNm * NM_IN_METERS
  return Math.min(1, Math.max(0.28, meters / 450))
}

export function updateLights(lights: RenderLight[], frame: LightFrame): void {
  const near = nearFactor(frame.distanceNm)
  for (const light of lights) {
    const sector = sectorVisibility(light.spec.type, frame.bearingDeg)
    const range = apparentIntensity(frame.distanceNm, light.rangeNm)
    const rhythm = rhythmFactor(light.spec, frame.timeSec)
    const intensity = sector * range * rhythm * (1 - frame.dayFactor * 0.88)
    light.intensity = intensity
    const visible = intensity > 0.002
    light.core.visible = visible
    light.halo.visible = visible
    if (!visible) continue
    const coreMaterial = light.core.material as SpriteMaterial
    const haloMaterial = light.halo.material as SpriteMaterial
    coreMaterial.opacity = Math.min(1, intensity * 1.6)
    haloMaterial.opacity = Math.min(0.85, intensity * 0.8)
    const grow = (0.6 + 0.4 * Math.min(1, intensity)) * near
    light.core.scale.setScalar(CORE_SCALE * grow)
    light.halo.scale.setScalar(HALO_SCALE * grow)
  }
}

export function disposeLights(lights: RenderLight[]): void {
  for (const light of lights) {
    ;(light.core.material as SpriteMaterial).dispose()
    ;(light.halo.material as SpriteMaterial).dispose()
    light.core.removeFromParent()
    light.halo.removeFromParent()
  }
}

export { COLOURS as LIGHT_COLOURS, TIER_SPACING_M }
